import { Routes, Route } from 'react-router-dom';
import { Box, Alert, Snackbar } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

import { AboutPage } from './pages/AboutPage';
import { FormsPage } from './pages/FormsPage';
import { TablePage } from './pages/TablePage';
import { NotFoundPage } from './pages/NotFoundPage';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import TwoSteps from './pages/TwoSteps';
import Dashboard from './pages/Dashboard';
import PageStub from './pages/PageStub';

// Layouts
import BlankLayout from './layouts/blank/BlankLayout';
import FullLayout from './layouts/full/FullLayout';
import ProtectedRoute from './components/ProtectedRoute';
import TicketsPage from '@/pages/TicketsPage.tsx';
import TicketDetailsPage from '@/pages/TicketDetailsPage.tsx';
import CredentialsPage from './pages/CredentialsPage';
import NoticeEditorPage from './pages/NoticeEditorPage';
import SchedulePage from './pages/SchedulePage';
import CreditPackagesPage from './pages/CreditPackagesPage';
import ShopPage from './pages/ShopPage';
import RolesPage from './pages/RolesPage';
import EmployeesPage from './pages/EmployeesPage';
import CustomerTypesPage from './pages/CustomerTypesPage';
import CustomerGroupsPage from './pages/CustomerGroupsPage';
import CustomersPage from './pages/CustomersPage';
import PricesPage from './pages/PricesPage';
import AutodataStubPage from './pages/AutodataStubPage';
import ProfilePage from './pages/ProfilePage';
import PCodesPage from './pages/PCodesPage';
import BoshCodesPage from './pages/BoshCodesPage';
import CreditTransactionPage from './pages/CreditTransactionPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import PurchaseInvoicesPage from './pages/PurchaseInvoicesPage';
import FileServicePage from './pages/FileServicePage';
import NewFileServicePage from './pages/NewFileServicePage';
import SolutionsPage from './pages/SolutionsPage';
import CreatedFileServicePage from './pages/CreatedFileServicePage';
import SolutionCreatePage from './pages/SolutionCreatePage';
import SolutionCategoryPage from './pages/SolutionCategoryPage';
import PriceCategoryPage from './pages/PriceCategoryPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import CompanyInfoPage from './pages/CompanyInfoPage';
import InfoPage from './pages/InfoPage';

function App() {
  const { t } = useTranslation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const handle500Error = () => {
      setSnackbarOpen(true);
    };

    window.addEventListener('api:error:500', handle500Error);

    return () => {
      window.removeEventListener('api:error:500', handle500Error);
    };
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {t('errors.serverError500')}
        </Alert>
      </Snackbar>
      <Routes>
        {/* Routes with Blank Layout (Auth pages) */}
        <Route path="/" element={<BlankLayout />}>
          <Route index element={<Login />} />
          <Route path="signup" element={<Signup />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="verification" element={<TwoSteps />} />
        </Route>

        {/* Routes with Full Layout (Main app pages) */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <FullLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="file-service" element={<FileServicePage />} />
          <Route path="new-file-service" element={<NewFileServicePage />} />
          <Route path="file-service/:fileId" element={<CreatedFileServicePage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="tickets/:ticketId" element={<TicketDetailsPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="solutions" element={<SolutionsPage />} />
          <Route path="solutions/new" element={<SolutionCreatePage />} />
          <Route path="solutions/category" element={<SolutionCategoryPage />} />
          <Route path="prices" element={<PricesPage />} />
          <Route path="prices/category" element={<PriceCategoryPage />} />
          <Route
            path="add-solution"
            element={<PageStub title="Add solution" />}
          />
          <Route path="scripts" element={<PageStub title="Scripts" />} />
          <Route
            path="slave-decode-encode"
            element={<PageStub title="Slave Decode & Encode" />}
          />
          <Route path="customers" element={<CustomersPage />} />
          <Route path="customer-types" element={<CustomerTypesPage />} />
          <Route path="customer-groups" element={<CustomerGroupsPage />} />
          <Route path="order-history" element={<OrderHistoryPage />} />
          <Route path="orders/:orderId" element={<OrderDetailsPage />} />
          <Route path="purchase-invoices" element={<PurchaseInvoicesPage />} />
          <Route
            path="credit-transaction"
            element={<CreditTransactionPage />}
          />
          <Route path="shop" element={<ShopPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="credit-packages" element={<CreditPackagesPage />} />
          <Route
            path="notice-editor"
            element={<NoticeEditorPage />}
          />
          <Route
            path="credentials-management"
            element={<CredentialsPage />}
          />
          <Route
            path="company-info"
            element={<CompanyInfoPage />}
          />
          <Route
            path="logo-setting"
            element={<PageStub title="Logo Setting" />}
          />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="autodata" element={<AutodataStubPage />} />
          <Route path="p-codes" element={<PCodesPage />} />
          <Route path="bosh-codes" element={<BoshCodesPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="terms-of-use" element={<InfoPage />} />
          <Route path="forms" element={<FormsPage />} />
          <Route path="table" element={<TablePage />} />
        </Route>

        {/* Legacy routes for backward compatibility */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <FullLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
        </Route>
        <Route
          path="/about"
          element={
            <ProtectedRoute>
              <FullLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AboutPage />} />
        </Route>
        <Route
          path="/forms"
          element={
            <ProtectedRoute>
              <FullLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<FormsPage />} />
        </Route>
        <Route
          path="/table"
          element={
            <ProtectedRoute>
              <FullLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TablePage />} />
        </Route>

        {/* 404 Page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Box>
  );
}

export default App;
